/*
 * A note about units:
 *
 * 1. The DOM deals in pixels.
 * 2. PDFs deal in points.
 *
 * To make things as simple as possible, this code essentially deals in pixels,
 * and will only convert to points right before writing to the PDF data.
 */
const _ = require('underscore');
const Q = require('q');
const TTFFont = require('./TTFFont');
const pdfImageProcessors = require('./pdfImageProcessors');
const urlUtilities = require('utilities/urlUtilities');
const logger = require('utilities/logger');

let extendedFonts;

var PDF_VERSION = '1.7';

var PAGE_HEIGHT = 1056,
	PAGE_WIDTH = 816,
	FONTS = {
		SERIF: {
			regular: 'F1',
			bold: 'F2',
			italics: 'F3',
			boldItalics: 'F4'
		},
		SANS_SERIF: {
			regular: 'F5',
			bold: 'F6',
			italics: 'F7',
			boldItalics: 'F8'
		}
	};

async function initExtendedFonts() {
	if (extendedFonts) {
		return extendedFonts;
	}

	const fontUrls = [
		'/assets/dist/fonts/ibm-plex-sans/IBMPlexSans-Regular.ttf',
		'/assets/dist/fonts/ibm-plex-sans/IBMPlexSans-Bold.ttf',
		'/assets/dist/fonts/ibm-plex-sans/IBMPlexSans-Italic.ttf',
		'/assets/dist/fonts/ibm-plex-sans/IBMPlexSans-BoldItalic.ttf'
	];

	const fontPromises = fontUrls.map(async url => {
		const fontUrl = urlUtilities.assetUrl(url);

		const response = await fetch(fontUrl);
		const fontBuffer = await response.arrayBuffer();
		return new Uint8Array(fontBuffer);
	});

	const [regular, bold, italics, boldItalics] = await Promise.all(fontPromises);

	extendedFonts = {
		regular,
		bold,
		italics,
		boldItalics
	};

	return extendedFonts;
}

function toUnicodeCmap(map) {
	var code, codes, range, unicode, unicodeMap, _i, _len;
	unicodeMap =
		'/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n/CIDSystemInfo <<\n  /Registry (Adobe)\n  /Ordering (UCS)\n  /Supplement 0\n>> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n1 begincodespacerange\n<0000><ffff>\nendcodespacerange';
	codes = Object.keys(map).sort(function (a, b) {
		return a - b;
	});

	range = [];
	for (_i = 0, _len = codes.length; _i < _len; _i++) {
		code = codes[_i];
		if (range.length >= 100) {
			unicodeMap += '\n' + range.length + ' beginbfchar\n' + range.join('\n') + '\nendbfchar';
			range = [];
		}

		if (map[code] !== undefined && map[code] !== null && typeof map[code].toString === 'function') {
			unicode = ('0000' + map[code].toString(16)).slice(-4);
			code = ('0000' + (+code).toString(16)).slice(-4);
			range.push('<' + code + '><' + unicode + '>');
		}
	}

	if (range.length) {
		unicodeMap += '\n' + range.length + ' beginbfchar\n' + range.join('\n') + '\nendbfchar\n';
	}
	unicodeMap += 'endcmap\nCMapName currentdict /CMap defineresource pop\nend\nend';
	return unicodeMap;
}

function pixelsToPoints(px) {
	return String(px * 0.75);
}

function pdfEscape16(text, metadata, font) {
	var widths = metadata[font].Unicode.widths;
	var ar = [''];
	for (var i = 0, l = text.length, t; i < l; ++i) {
		t = metadata[font].characterToGlyph(text.charCodeAt(i));
		metadata[font].glyIdsUsed.push(t);
		metadata[font].toUnicode[t] = text.charCodeAt(i);
		if (widths.indexOf(t) == -1) {
			widths.push(t);

			let scale = 1;

			if (t === 837) {
				scale = 0.75;
			}

			widths.push([parseInt(metadata[font].widthOfGlyph(t) * scale, 10)]);
		}

		//Spaces are not allowed in cmap.
		if (t !== 0) {
			t = t.toString(16);
			ar.push(t.padStart(4, '0'));
		}
	}

	return ar.join('');
}

function buildFontResourcesObject(addObject, content, extendedFont, metadata, iStrings) {
	const fontMap = new Map([
		[
			'fontObjectNumberSerif',
			{
				fontName: 'Times-Roman',
				font: FONTS.SERIF.regular
			}
		],
		[
			'fontBoldObjectNumberSerif',
			{
				fontName: 'Times-Bold',
				font: FONTS.SERIF.bold
			}
		],
		[
			'fontItalicsObjectNumberSerif',
			{
				fontName: 'Times-Italic',
				font: FONTS.SERIF.italics
			}
		],
		[
			'fontBoldItalicsObjectNumberSerif',
			{
				fontName: 'Times-BoldItalic',
				font: FONTS.SERIF.boldItalics
			}
		],
		[
			'fontRegular',
			{
				fontName: 'Helvetica',
				extendedFontName: 'IBMPlexSans-Regular',
				font: FONTS.SANS_SERIF.regular,
				flags: 32
			}
		],
		[
			'fontBoldObjectNumberSans',
			{
				fontName: 'Helvetica-Bold',
				font: FONTS.SANS_SERIF.bold,
				extendedFontName: 'IBMPlexSans-Bold',
				flags: 96
			}
		],
		[
			'fontItalicsObjectNumberSans',
			{
				fontName: 'Helvetica-Oblique',
				font: FONTS.SANS_SERIF.italics,
				extendedFontName: 'IBMPlexSans-Italic',
				flags: 262208
			}
		],
		[
			'fontBoldItalicsObjectNumberSans',
			{
				fontName: 'Helvetica-BoldOblique',
				font: FONTS.SANS_SERIF.boldItalics,
				extendedFontName: 'IBMPlexSans-BoldItalic',
				flags: 262240
			}
		]
	]);
	let resourcesObjectInfo = ['<<', '/ProcSet [/PDF /Text /ImageB /ImageC /ImageI]', '/XObject <<', iStrings.join('\n'), '>>', '/Font <<'];

	const buildExtended = ({ font, extendedFontName, flags, italicAngle }) => {
		let pdfOutput;
		let pdfOutput2 = '';
		let fontStreamNumber;
		let cmap;
		pdfOutput = metadata[font].subset.encode(metadata[font].glyIdsUsed, 1);
		for (let i = 0; i < pdfOutput.length; i++) {
			pdfOutput2 += String.fromCharCode(pdfOutput[i]);
		}

		fontStreamNumber = addObject(content, [
			'<<',
			'/Length ' + pdfOutput2.length,
			'/Length1 ' + pdfOutput2.length,
			'>>',
			'stream',
			pdfOutput2,
			'endstream'
		]);

		cmap = toUnicodeCmap(metadata[font].toUnicode);

		const extendedFontDescriptorNumber = addObject(content, [
			'<<',
			'/Type /FontDescriptor',
			`/FontName /${extendedFontName}`,
			`/FontFile2 ${fontStreamNumber} 0 R`,
			'/FontBBox [-280 -258 1280 1132]',
			`/Flags ${flags}`,
			'/StemV 0',
			'/Ascent 1025',
			'/Descent -275',
			'/CapHeight 698',
			'>>'
		]);
		const extendedFontUnicodeNumber = addObject(content, [
			'<<',
			'/Length ' + cmap.length,
			'/Length1 ' + cmap.length,
			'>>',
			'stream',
			cmap,
			'endstream'
		]);
		const fontExtended = addObject(content, [
			'<<',
			'/Type /Font',
			`/BaseFont /${extendedFontName}`,
			'/FontDescriptor ' + extendedFontDescriptorNumber + ' 0 R',
			'/W ' + window.PDFObject.convert(metadata[font].Unicode.widths),
			'/DW 1000',
			'/Subtype /CIDFontType2',
			'/CIDSystemInfo',
			'<<',
			'/Supplement 0',
			'/Registry (Adobe)',
			'/Ordering (Identity-H)',
			'>>',
			'>>'
		]);

		const fontData = addObject(content, [
			'<<',
			'/Type /Font',
			'/Subtype /Type0',
			`/ToUnicode ${extendedFontUnicodeNumber} 0 R`,
			`/BaseFont /${extendedFontName}`,
			'/Encoding /Identity-H',
			`/DescendantFonts [${fontExtended} 0 R]`,
			'>>'
		]);

		resourcesObjectInfo.push(`      /${font} ${fontData} 0 R`);
	};

	const buildStandard = ({ font, fontName }) => {
		const fontData = addObject(content, ['<<', '  /Type /Font', '  /Subtype /Type1', '/Encoding/WinAnsiEncoding', `  /BaseFont /${fontName}`, '>>']);

		resourcesObjectInfo.push(`      /${font} ${fontData} 0 R`);
	};

	fontMap.forEach(fontInfo => {
		if (extendedFont && fontInfo.extendedFontName) {
			buildExtended(fontInfo);
		} else {
			buildStandard(fontInfo);
		}
	});

	resourcesObjectInfo.push('>>', '>>');

	return addObject(content, resourcesObjectInfo);
}
/**
 * @param {String}	selector					CSS selector for target element.
 * @param {Object}	[options]
 * @param {Boolean}	[options.debug]				Enables in-browser preview of PDF.
 * @param {String}  [options.documentHeader]	HTML to appear as a header at the start of the document.
 * @param {String}  [options.filename]			PDF filename (IE\Edge only).
 * @param {String}  [options.footer]			HTML to appear as a footer at the end of each page.
 * @param {String}  [options.header]			HTML to appear as a header at the start of each page.
 * @param {Array}	[options.cssClasses]		List of CSS classes to apply.
 * @param {Number}  [options.scaleImg]				Scales PDF image as percentage of original, where 1 is no scaling.
 * @param {Number}  [options.scaleFont]				Scales PDF text as percentage of original, where 1 is no scaling.
 * @param {Array}   [options.noPageBreak]		CSS selectors that should not contain page breaks.
 * @param {Number}  [options.widowThreshold]	Percentage of page height over which the last page's contents must reach to not be considered a widow.
 *                                           	If a widow is detected, the PDF will be progressively scaled down in increments of 10% until the widow resolves.
 * @param {Boolean}  [options.excludeImages]	Prevent images and related content from rendering
 */
function PDF(selector, options) {
	this.selector = selector;
	this.options = options;

	var printContainer = $('<div class="print-container"></div>'),
		printContainerContent = $(selector).clone();

	printContainerContent.find('*[data-vocab-sense-id]').each(function (i, node) {
		var parentElement = node.parentElement;

		$(node).contents().unwrap();

		parentElement.normalize();
	});

	printContainer.append(printContainerContent);

	$('body').append(printContainer);

	if (options.cssClasses) {
		options.cssClasses.forEach(function (cssClass) {
			printContainer.addClass(cssClass);
		});
	}

	if (options.extended_font) {
		printContainer.addClass('extended-font');
	}

	this.el = $(printContainer);

	if (options) {
		this.debug = options.debug;
		this.scaleImg = options?.scaleImg;
		this.scaleFont = options?.scaleFont;
		this.noPageBreak = options.noPageBreak;
		this.widowThreshold = options.widowThreshold;

		var self = this;

		if (typeof this.scaleImg === 'number') {
			$('img', printContainer).each(function (i, node) {
				let height = $(node).css('height');
				let width = $(node).css('width');
				const reducedHeight = parseFloat(height) * self.scaleImg;
				const reducedWidth = parseFloat(width) * self.scaleImg;
				const minHeight = 150;
				const minWidth = 150;

				if (reducedHeight >= minHeight && reducedWidth >= minWidth) {
					$(node)
						.css('height', reducedHeight + 'px')
						.css('width', reducedWidth + 'px');
				}
			});
		}

		if (typeof this.scaleFont === 'number') {
			const fontSize = parseFloat(printContainerContent.css('font-size'));
			const newFontSize = this.scaleFont * fontSize;
			printContainerContent.css('font-size', newFontSize + 'px');
		}

		if (this.debug) {
			printContainer.addClass('debug');
		}

		if (options.documentHeader) {
			this.el.prepend(options.documentHeader);
		}

		if (options.header) {
			this.el.prepend('<div class="header-container">' + options.header + '</div>');
		}

		if (options.footer) {
			this.el.append('<div class="footer-container">' + options.footer + '</div>');
		}
	}

	var elStyle = getComputedStyle($(this.el)[0]);

	this.gutterTop = parseFloat(elStyle['padding-top']) + parseFloat(elStyle['margin-top']);
	this.gutterBottom = parseFloat(elStyle['padding-bottom']) + parseFloat(elStyle['margin-bottom']);

	if (options.footer) {
		var footerPositionTop = PAGE_HEIGHT - ($('.footer-container', this.el).offset().top - $(window).scrollTop());

		if (footerPositionTop > this.gutterBottom) {
			this.gutterBottom = footerPositionTop;
		}
	}

	this.objectCounter = 0;
	this.imageCounter = 0;
	this.images = {};
	this.pages = [];
	this.objectMap = [];
	this.minPagePositionYPerPage = {};
}

PDF.prototype.addObject = function (content, s) {
	this.objectMap.push(content.join(' ').length + 1);

	content.push(++this.objectCounter + ' 0 obj');

	if (!(s instanceof Array)) {
		s = [s];
	}

	s.forEach(function (line) {
		content.push(line);
	});

	content.push('endobj');

	return this.objectCounter;
};

PDF.prototype.addImage = function (content, url, image) {
	var objectNumber = this.addObject(content, [
		'<</Type /XObject',
		'/Subtype /Image',
		'/Width ' + image.w,
		'/Height ' + image.h,
		'/ColorSpace /' + image.cs,
		'/BitsPerComponent ' + image.bpc,
		'/Filter /' + image.f,
		'/Length ' + image.data.length + '>>',
		'stream',
		image.data,
		'endstream'
	]);

	image.objectNumber = objectNumber;
};

PDF.prototype.addPage = function (content, p, resourcesObjectNumber, pagesObjectNumber) {
	var pageLength = p.join(' ').length + 1;

	p.unshift('stream');
	p.unshift('>>');
	p.unshift('  /Length ' + pageLength);
	p.unshift('<<');

	p.push('endstream');

	this.addObject(content, [
		'<<',
		'  /Type /Page',
		'  /Parent ' + pagesObjectNumber + ' 0 R',
		'  /Resources ' + resourcesObjectNumber + ' 0 R',
		'  /Contents ' + (this.objectCounter + 2) + ' 0 R',
		'>>'
	]);

	return this.addObject(content, p);
};

PDF.prototype.generate = async function () {
	var content = ['%PDF-' + PDF_VERSION],
		$el = this.el,
		self = this,
		promises = [];

	if (self.options.extended_font) {
		await initExtendedFonts();
		const metadata = {
			[FONTS.SANS_SERIF.regular]: TTFFont.open(extendedFonts.regular),
			[FONTS.SANS_SERIF.bold]: TTFFont.open(extendedFonts.bold),
			[FONTS.SANS_SERIF.italics]: TTFFont.open(extendedFonts.italics),
			[FONTS.SANS_SERIF.boldItalics]: TTFFont.open(extendedFonts.boldItalics)
		};

		for (let fontMeta in metadata) {
			metadata[fontMeta].Unicode = metadata[fontMeta].Unicode || {
				encoding: {},
				kerning: {},
				widths: []
			};

			metadata[fontMeta].glyIdsUsed = [0];
		}

		this.metadata = metadata;
	}

	$el.find('img').each(function (i, node) {
		var $img = $(node),
			url = $img.attr('src');

		if (!url || self.images[url] || url.indexOf('.svg') !== -1) {
			return;
		}

		if (self.options.excludeImages) {
			if ($img.parents('.header-container, .footer-container').length === 0 && !url.includes('written-answer-lines-')) {
				const $fig = $img.parents('figure');
				const $elToHide = $fig.length > 0 ? $fig : $img;

				$elToHide.hide();
				return;
			}
		}

		var deferred = Q.defer();

		$img.on('load', function () {
			var img = new Image();

			img.onerror = img.onload = function () {
				if (img.complete) {
					//to support data urls in images, set width and height
					//as those values are not recognized automatically
					if (img.src.indexOf('data:image/') === 0) {
						img.width = $img.width() || img.width || 0;
						img.height = $img.height() || img.height || 0;
					}
					//if valid image add to known images array
					if (img.width + img.height) {
						var dataAsBinaryString,
							imageData = pdfImageProcessors.createDataURIFromElement(img);

						var base64Info = pdfImageProcessors.extractInfoFromBase64DataURI(imageData),
							format;

						if (base64Info) {
							format = base64Info[2];
							imageData = atob(base64Info[3]);
						} else if (
							imageData.charCodeAt(0) === 0x89 &&
							imageData.charCodeAt(1) === 0x50 &&
							imageData.charCodeAt(2) === 0x4e &&
							imageData.charCodeAt(3) === 0x47
						) {
							format = 'png';
						}

						format = (format || 'JPEG').toLowerCase();

						if (pdfImageProcessors.supportsArrayBuffer()) {
							dataAsBinaryString = imageData;
							imageData = pdfImageProcessors.binaryStringToUint8Array(imageData);
						}

						self.images[url] = pdfImageProcessors['process' + format.toUpperCase()](imageData, null, null, null, dataAsBinaryString);
						self.images[url].index = self.imageCounter++;
					}
				}

				deferred.resolve({
					url: url,
					info: self.images[url]
				});
			};

			img.crossOrigin = 'anonymous';

			img.src = url + '?cb=1';
		});

		promises.push(deferred.promise);
	});

	return Q.all(promises)
		.then(function (images) {
			var pages = self.pages,
				node,
				walk = document.createTreeWalker(
					$el[0],
					NodeFilter.SHOW_ALL,
					function (nodeToFilter) {
						if (nodeToFilter.nodeType === 3) {
							return NodeFilter.FILTER_ACCEPT;
						}

						if (nodeToFilter.nodeType === 8) {
							return NodeFilter.FILTER_REJECT;
						}

						if (nodeToFilter.tagName === 'IMG' && $(nodeToFilter).attr('src') && $(nodeToFilter).attr('src').indexOf('.svg') !== -1) {
							return NodeFilter.FILTER_REJECT;
						}

						var nodeStyle = getComputedStyle(nodeToFilter);

						if (nodeStyle.display !== 'none' && nodeStyle.visibility !== 'hidden') {
							return NodeFilter.FILTER_ACCEPT;
						}

						return NodeFilter.FILTER_REJECT;
					},
					false
				);

			let verticalBuffer = 0,
				previousPageIndex = 0;
			const header = [],
				footer = [],
				noPageBreak = self.noPageBreak,
				minPagePositionYPerPage = self.minPagePositionYPerPage;

			while ((node = walk.nextNode())) {
				let page = void 0,
					pageIndex = void 0,
					processingFooter = false,
					processingHeading = false,
					$node = $(node);

				if ($node.parents('.header-container').length || $node.hasClass('header-container')) {
					processingHeading = true;
				} else {
					processingHeading = false;
				}

				if ($node.parents('.footer-container').length || $node.hasClass('footer-container')) {
					processingFooter = true;
				} else {
					processingFooter = false;
				}

				if (node.nodeType === 3) {
					var textContent = node.textContent;

					if (textContent.trim()) {
						var range = document.createRange(),
							parentStyles = getComputedStyle(node.parentElement || node.parentNode),
							lineHeight = parseFloat(parentStyles['line-height']),
							fontSize = parseFloat(parentStyles['font-size']),
							start = 0,
							rectangles = void 0,
							line = '',
							pagePositionY = void 0,
							listProcessed = false;

						range.setStart(node, 0);

						var l = textContent.length;

						for (var end = 1; end <= l; end++) {
							line = textContent.substring(start, end);

							range.setEnd(node, end);

							const { newRectangles } = Array.from(range.getClientRects()).reduce(
								(acc, item) => {
									const { top } = item;
									if (acc.tops.has(top)) {
										return acc;
									}

									acc.newRectangles.push(item);
									acc.tops.add(top);
									return acc;
								},
								{
									newRectangles: [],
									tops: new Set()
								}
							);

							if (!newRectangles.length) {
								continue;
							}

							if (end === l || (rectangles && rectangles.length !== newRectangles.length)) {
								var lineRectangle = void 0;

								if (rectangles && rectangles.length !== newRectangles.length) {
									lineRectangle = newRectangles[newRectangles.length - 2];
								} else if (end === l) {
									lineRectangle = newRectangles[newRectangles.length - 1];
								}

								if (end === l) {
									line = textContent.substring(start, end + 1);
								} else {
									line = textContent.substring(start, --end);
								}

								var y = lineRectangle.top + verticalBuffer + lineHeight;

								pageIndex = Math.floor(y / (PAGE_HEIGHT - self.gutterBottom));

								y -= PAGE_HEIGHT * pageIndex;

								if (processingHeading) {
									y = lineRectangle.top;
									page = header;
								} else if (processingFooter) {
									y = lineRectangle.top + lineRectangle.height;
									page = footer;
								} else {
									if (y > PAGE_HEIGHT - self.gutterBottom) {
										// Line lies on page break, bump to beginning of next page
										pageIndex++;

										verticalBuffer += PAGE_HEIGHT + self.gutterTop - y;

										y = self.gutterTop;
									} else if (y < self.gutterTop) {
										// Line lies on top gutter, bump down to bottom of gutter
										verticalBuffer += self.gutterTop - y;

										y = self.gutterTop;
									}

									page = pages[pageIndex];
									previousPageIndex = pageIndex;

									if (!page) {
										page = pages[pageIndex] = [];
									}
								}

								var isBold = parentStyles['font-weight'] === 'bold' || Number(parentStyles['font-weight']) > 400,
									isItalics = parentStyles['font-style'] === 'italic',
									baseFont = FONTS.SERIF;

								const parentFont = parentStyles.getPropertyValue('font-family');

								if (parentFont.includes('Helvetica') || parentFont.includes('IBMPlexSans')) {
									baseFont = FONTS.SANS_SERIF;
								}

								var font = baseFont.regular;

								if (isBold && isItalics) {
									font = baseFont.boldItalics;
								} else if (isBold) {
									font = baseFont.bold;
								} else if (isItalics) {
									font = baseFont.italics;
								}

								var outputText = line;

								if (!self.options.extended_font || baseFont === FONTS.SERIF) {
									outputText = outputText
										.replace(/\\/g, '\\\\')
										.replace(/\(/g, '\\(')
										.replace(/\)/g, '\\)')
										.replace(/[\u0100-\uffff]/g, function (ch) {
											switch (ch) {
												case '“':
												case '”':
												case '”�':
													return '"';
												case '’':
												case '‘':
													return "'";
												case '–':
												case '—':
													return '-';
												case '…':
													return '...';
												default:
													return '';
											}
										});
								}

								var left = lineRectangle.left,
									list = $(node).parents('li'),
									listStyleType = parentStyles['list-style-type'];

								if (listStyleType !== 'none' && list.length && !listProcessed) {
									var firstListNode = void 0,
										listIterator = document.createNodeIterator(list[0], NodeFilter.SHOW_TEXT, null, null);

									while ((firstListNode = listIterator.nextNode())) {
										if (firstListNode.textContent.trim()) {
											break;
										}
									}

									if (firstListNode === node) {
										if (list.parent('ol').length) {
											if (listStyleType === 'upper-alpha') {
												outputText = String.fromCharCode(97 + list.index()).toUpperCase() + '. ' + outputText;
											} else {
												outputText = list.index() + 1 + '. ' + outputText;
											}

											left -= fontSize;
										} else {
											outputText = '· ' + outputText;
											left -= fontSize / 2;
										}

										listProcessed = true;
									}
								}

								pagePositionY = PAGE_HEIGHT - y;

								page.push('BT');
								page.push(pixelsToPoints(left) + ' ' + pixelsToPoints(pagePositionY) + ' TD');
								page.push('/' + font + ' ' + pixelsToPoints(fontSize) + ' Tf');

								if (self.options.extended_font && baseFont !== FONTS.SERIF) {
									page.push('<' + pdfEscape16(outputText.trim(), self.metadata, font) + '> Tj');
								} else {
									page.push('(' + outputText + ') Tj');
								}

								page.push('ET');

								start = end;

								if (textContent[start] === ' ') {
									start++;
									end++;
								}

								line = '';
							}

							rectangles = newRectangles;
						}
					}
				} else if (
					node.nodeName === 'IMG' &&
					!(self.options.excludeImages && !(processingHeading || processingFooter || node.src.includes('written-answer-lines-')))
				) {
					var imgRange = document.createRange();

					imgRange.selectNode(node);

					var imgRectangle = imgRange.getClientRects()[0];

					if (!imgRectangle) {
						// Range.selectNode doesn't seem to work in IE11 for <img>
						var offset = $(node).offset();

						imgRectangle = {
							top: offset.top - $(window).scrollTop(),
							left: offset.left
						};
					}

					var info = self.images[node.attributes.src.value],
						$img = $(node),
						rh = $img.height() || ($img.css('height') && parseFloat($img.css('height'))),
						rw = $img.width() || ($img.css('width') && parseFloat($img.css('width'))),
						leftOffset = parseFloat($img.css('padding-left')),
						topOffset = parseFloat($img.css('padding-top')),
						top = imgRectangle.top,
						imageY = top + rh + verticalBuffer;

					pageIndex = Math.floor(imageY / (PAGE_HEIGHT - self.gutterBottom));

					imageY -= PAGE_HEIGHT * pageIndex;

					if (processingHeading) {
						imageY = top + rh;
						page = header;
					} else if (processingFooter) {
						imageY = top + rh;
						page = footer;
					} else {
						if (imageY + topOffset > PAGE_HEIGHT - self.gutterBottom) {
							// Line lies on page break, bump to beginning of next page
							pageIndex++;

							verticalBuffer += PAGE_HEIGHT + self.gutterTop + rh - imageY;

							imageY = self.gutterTop + rh;
						} else if (imageY < rh) {
							// Line lies on top gutter, bump down to bottom of gutter
							verticalBuffer += self.gutterTop + rh - imageY;

							imageY = self.gutterTop + rh;
						}

						page = pages[pageIndex];
						previousPageIndex = pageIndex;

						if (!page) {
							page = pages[pageIndex] = [];
						}
					}

					pagePositionY = PAGE_HEIGHT - imageY;

					page.push('q');
					page.push(pixelsToPoints(rw.toFixed(2)));
					page.push('0 0');
					page.push(pixelsToPoints(rh.toFixed(2)));
					page.push(pixelsToPoints(imgRectangle.left + leftOffset));
					page.push(pixelsToPoints(pagePositionY - topOffset));
					page.push('cm /I' + info.index);
					page.push('Do Q');
				} else if (node.nodeType === 1) {
					if (noPageBreak) {
						let contentY;
						for (let i = 0; i < noPageBreak.length; i++) {
							if ($node.is(noPageBreak[i])) {
								// contentY is the current length of content
								contentY = $(node).offset().top - $(window).scrollTop() + verticalBuffer;

								const nextPageIndex = Math.floor((contentY + $node.height()) / (PAGE_HEIGHT - self.gutterBottom));

								if (previousPageIndex !== nextPageIndex) {
									// Calculates spacing to be added when content runs into another page
									// (Length of available full pages - actual length of content + header length )
									verticalBuffer += PAGE_HEIGHT * nextPageIndex - contentY + self.gutterTop;
								}

								previousPageIndex = nextPageIndex;
								break;
							}
						}
					}

					var nodeStyle = getComputedStyle(node);

					if (
						nodeStyle['border-bottom-style'] !== 'none' ||
						nodeStyle['border-top-style'] !== 'none' ||
						nodeStyle['border-left-style'] !== 'none' ||
						nodeStyle['border-right-style'] !== 'none'
					) {
						var box = node.getBoundingClientRect();

						if (processingHeading) {
							page = header;
						} else if (processingFooter) {
							page = footer;
						} else {
							pageIndex = Math.floor(box.top / PAGE_HEIGHT);

							page = pages[pageIndex];

							if (!page) {
								page = pages[pageIndex] = [];
							}
						}

						var boxRange = document.createRange();

						boxRange.selectNode(node);

						var borderTop = nodeStyle['border-top-style'] !== 'none',
							borderBottom = nodeStyle['border-bottom-style'] !== 'none',
							borderLeft = nodeStyle['border-left-style'] !== 'none',
							borderRight = nodeStyle['border-right-style'] !== 'none';

						if (borderTop) {
							var borderTopY = PAGE_HEIGHT - box.top;

							page.push('q');
							page.push('0.50 B');
							page.push(pixelsToPoints(box.left) + ' ' + pixelsToPoints(borderTopY) + ' m');
							page.push(pixelsToPoints(box.right) + ' ' + pixelsToPoints(borderTopY) + ' l');
							page.push('S');
						}

						if (borderBottom) {
							var borderBottomY = PAGE_HEIGHT - box.bottom;

							page.push('q');
							page.push('0.50 B');
							page.push(pixelsToPoints(box.left) + ' ' + pixelsToPoints(borderBottomY) + ' m');
							page.push(pixelsToPoints(box.right) + ' ' + pixelsToPoints(borderBottomY) + ' l');
							page.push('S');
						}

						if (borderLeft) {
							page.push('q');
							page.push('0.50 B');
							page.push(pixelsToPoints(box.left) + ' ' + pixelsToPoints(PAGE_HEIGHT - box.top) + ' m');
							page.push(pixelsToPoints(box.left) + ' ' + pixelsToPoints(PAGE_HEIGHT - box.bottom) + ' l');
							page.push('S');
						}

						if (borderRight) {
							page.push('q');
							page.push('0.50 B');
							page.push(pixelsToPoints(box.right) + ' ' + pixelsToPoints(PAGE_HEIGHT - box.top) + ' m');
							page.push(pixelsToPoints(box.right) + ' ' + pixelsToPoints(PAGE_HEIGHT - box.bottom) + ' l');
							page.push('S');
						}
					}
				}

				if (typeof pageIndex !== 'undefined' && !processingHeading && !processingFooter) {
					if (!minPagePositionYPerPage[pageIndex] || minPagePositionYPerPage[pageIndex] > pagePositionY) {
						minPagePositionYPerPage[pageIndex] = pagePositionY;
					}
				}
			}

			if (pages.length > 1 && 1 - minPagePositionYPerPage[pages.length - 1] / PAGE_HEIGHT < self.widowThreshold) {
				self.el.remove();

				return new PDF(
					self.selector,
					_.assign(self.options || {}, {
						scaleImg: self.options?.scaleImg - 0.1 || 0.9,
						scaleFont: self.options?.scaleFont - 0.05 || 0.95
					})
				).generate();
			}

			var iStrings = [];

			images.forEach(function (o) {
				self.addImage(content, o.url, o.info);

				iStrings.push('/I' + o.info.index + ' ' + o.info.objectNumber + ' 0 R');
			});

			const addObject = PDF.prototype.addObject.bind(self);
			const resourcesObjectNumber = buildFontResourcesObject(addObject, content, self.options.extended_font, self.metadata, iStrings);

			var kids = [],
				pageCount = pages.length;

			for (var j = 0; j < pageCount; j++) {
				kids.push(resourcesObjectNumber + 2 + j * 2 + ' 0 R');
			}

			var pagesObjectNumber = self.addObject(content, [
				'<<',
				'  /Type /Pages',
				'  /MediaBox [ 0 0 ' + pixelsToPoints(PAGE_WIDTH) + ' ' + pixelsToPoints(PAGE_HEIGHT) + ' ]',
				'  /Count ' + pageCount,
				'  /Kids [ ' + kids.join('\n') + ' ]',
				'>>'
			]);

			pages.forEach(function (p) {
				p.unshift.apply(p, header);
				p.push.apply(p, footer);

				self.addPage(content, p, resourcesObjectNumber, pagesObjectNumber);
			});

			var rootObjectNumber = self.addObject(content, ['<<', '  /Type /Catalog', '  /Pages ' + pagesObjectNumber + ' 0 R', '>>']),
				xrefOffset = content.join('\n').length;

			content.push('xref');
			content.push('0 ' + (self.objectCounter + 1));
			content.push('0000000000 65535 f');

			self.objectMap.forEach(function (oo) {
				var s = oo + '';

				while (s.length < 10) s = '0' + s;

				content.push(s + ' 00000 n');
			});

			content.push('trailer');
			content.push('<<');
			content.push('  /Size ' + (self.objectCounter + 1));
			content.push('  /Root ' + rootObjectNumber + ' 0 R');
			content.push('>>');
			content.push('startxref');
			content.push(xrefOffset);
			content.push('%%EOF');

			var data = content.join('\n'),
				len = data.length,
				ab = new ArrayBuffer(len),
				u8 = new Uint8Array(ab);

			while (len--) u8[len] = data.charCodeAt(len);

			if (self.debug) {
				logger.info(data);
			}

			return {
				blob: new Blob([ab], {
					type: 'application/pdf'
				}),
				pdf: self
			};
		})
		.fin(function () {
			$el.find('figure, img').show();
			if (!self.debug) {
				self.el.remove();
			}
		});
};

/**
 * Combines multiple PDFs into one.
 *
 * @param {Array} 	pdfs Array of PDF instances that have had "generate" called on them.
 * @param {Object}	[options]
 * @param {Boolean}	[options.pageNumbers]
 *
 * @return {Blob}
 */
PDF.concat = function (pdfs, options) {
	var content = ['%PDF-' + PDF_VERSION],
		dummyPDF = {
			objectCounter: 0,
			objectMap: [],
			addObject: PDF.prototype.addObject
		},
		images = [],
		pages = [],
		toUnicode = {},
		glyIdsUsed = [],
		widths = [],
		extendedFont = false;

	pdfs.forEach(function (pdf, i) {
		var pagesForPDF = pdf.pages,
			imagePrefix = String(i + 1);

		if (pdf.options.extended_font) {
			extendedFont = true;
		}

		for (var url in pdf.images) {
			var newInfo = pdf.images[url];

			newInfo.index = imagePrefix + newInfo.index;

			images.push({
				url: url,
				info: newInfo
			});
		}

		pagesForPDF.forEach(function (page) {
			// Remove extraneous PDF header\footer data, this will be recalculated on subsequent addPage calls
			page.shift();
			page.shift();
			page.shift();
			page.shift();
			page.pop();

			page.forEach(function (line, c) {
				page[c] = page[c].replace('cm /I', 'cm /I' + imagePrefix);
			});
		});

		pages = pages.concat(pagesForPDF);
	});

	var iStrings = [];

	images.forEach(function (imageObject) {
		var info = imageObject.info;

		PDF.prototype.addImage.call(dummyPDF, content, imageObject.url, info);

		iStrings.push('/I' + info.index + ' ' + info.objectNumber + ' 0 R');
	});

	const addObject = PDF.prototype.addObject.bind(dummyPDF);
	const dummyMetadata = Object.values(FONTS.SANS_SERIF).reduce((acc, font) => {
		if (extendedFont) {
			const fontMeta = pdfs.reduce((metadata, pdf) => {
				const meta = pdf.metadata[font];

				if (!metadata) {
					return meta;
				}

				metadata.glyIdsUsed = [...metadata.glyIdsUsed, ...meta.glyIdsUsed];
				metadata.Unicode.widths = [...metadata.Unicode.widths, ...meta.Unicode.widths];

				return metadata;
			}, null);
			acc[font] = fontMeta;

			return acc;
		}

		acc[font] = {
			subset: {
				encode: () => {}
			},
			glyIdsUsed,
			toUnicode,
			Unicode: {
				widths
			}
		};

		return acc;
	}, {});

	const resourcesObjectNumber = buildFontResourcesObject(addObject, content, extendedFont, dummyMetadata, iStrings);

	var kids = [],
		pageCount = pages.length;

	for (var i = 0; i < pageCount; i++) {
		kids.push(resourcesObjectNumber + 2 + i * 2 + ' 0 R');
	}

	var pagesObjectNumber = addObject(content, [
		'<<',
		'  /Type /Pages',
		'  /MediaBox [ 0 0 ' + pixelsToPoints(PAGE_WIDTH) + ' ' + pixelsToPoints(PAGE_HEIGHT) + ' ]',
		'  /Count ' + pageCount,
		'  /Kids [ ' + kids.join('\n') + ' ]',
		'>>'
	]);

	pages.forEach(function (p, j) {
		if (options && options.pageNumbers) {
			p.push('BT');
			p.push(pixelsToPoints(45) + ' ' + pixelsToPoints(10) + ' TD');
			p.push('/' + FONTS.SANS_SERIF.regular + ' ' + pixelsToPoints(10) + ' Tf');
			let pageNumbers = `Page ${j + 1} of ${pageCount}`;
			if (extendedFont) {
				pageNumbers = p.push('<' + pdfEscape16(pageNumbers, pdfs[0].metadata, FONTS.SANS_SERIF.regular) + '> Tj');
			} else {
				p.push('(' + pageNumbers + ') Tj');
			}
			p.push('ET');
		}

		PDF.prototype.addPage.call(dummyPDF, content, p, resourcesObjectNumber, pagesObjectNumber);
	});

	var rootObjectNumber = addObject(content, ['<<', '  /Type /Catalog', '  /Pages ' + pagesObjectNumber + ' 0 R', '>>']);

	var offset = content.join('\n').length;

	content.push('xref');
	content.push('0 ' + (dummyPDF.objectCounter + 1));
	content.push('0000000000 65535 f');

	dummyPDF.objectMap.forEach(function (oo) {
		var s = oo + '';

		while (s.length < 10) s = '0' + s;

		content.push(s + ' 00000 n');
	});

	content.push('trailer');
	content.push('<<');
	content.push('  /Size ' + (dummyPDF.objectCounter + 1));
	content.push('  /Root ' + rootObjectNumber + ' 0 R');
	content.push('>>');
	content.push('startxref');
	content.push(offset);
	content.push('%%EOF');

	var data = content.join('\n'),
		len = data.length,
		ab = new ArrayBuffer(len),
		u8 = new Uint8Array(ab);

	while (len--) u8[len] = data.charCodeAt(len);

	return new Blob([ab], {
		type: 'application/pdf'
	});
};

PDF.initExtendedFonts = initExtendedFonts;

module.exports = PDF;
